import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Alert, Select, MenuItem, FormControl, InputLabel, ToggleButtonGroup, ToggleButton, Button } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import Quagga from '@ericblade/quagga2';
import { isVirtualCamera } from '../utils/camera';
import { analytics } from '../services/analytics';

interface BarcodeScannerProps {
  onDetected: (barcode: string) => void;
  onError?: (error: string) => void;
  active: boolean;
  resetTrigger?: number;
}

const CAMERA_STORAGE_KEY = 'barcodeScannerCameraId';
const ZOOM_STORAGE_KEY = 'barcodeScannerZoom';
const DEFAULT_ZOOM = 2;

const isValidEAN13 = (code: string): boolean => {
  if (code.length !== 13 || !/^\d{13}$/.test(code)) return false;
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(code[i], 10) * (i % 2 === 0 ? 1 : 3);
  }
  return (10 - (sum % 10)) % 10 === parseInt(code[12], 10);
};

const isValidUPCA = (code: string): boolean => {
  if (code.length !== 12 || !/^\d{12}$/.test(code)) return false;
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    sum += parseInt(code[i], 10) * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10 === parseInt(code[11], 10);
};

interface CameraDevice {
  deviceId: string;
  label: string;
}

const REQUIRED_VOTES = 3;

const stopStream = (stream: MediaStream | null) => {
  stream?.getTracks().forEach(track => track.stop());
};

const isMobileDevice = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ onDetected, onError, active, resetTrigger }) => {
  const scannerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waitingForClick, setWaitingForClick] = useState(!isMobileDevice());
  const [backCameras, setBackCameras] = useState<CameraDevice[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [camerasLoaded, setCamerasLoaded] = useState(false);
  const [scanStatus, setScanStatus] = useState<string>('Point camera at barcode');
  const [maxZoom, setMaxZoom] = useState<number>(1);
  const [currentZoom, setCurrentZoom] = useState<number>(DEFAULT_ZOOM);
  const [availableZoomLevels, setAvailableZoomLevels] = useState<number[]>([1]);
  const [scannerStarted, setScannerStarted] = useState(false);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onDetectedRef = useRef(onDetected);
  const votesRef = useRef<Record<string, number>>({});
  const hasDetectedRef = useRef(false);
  const quaggaInitializedRef = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const isStoppingRef = useRef(false);

  useEffect(() => { onDetectedRef.current = onDetected; }, [onDetected]);

  useEffect(() => {
    try {
      const savedZoom = localStorage.getItem(ZOOM_STORAGE_KEY);
      if (savedZoom) setCurrentZoom(parseFloat(savedZoom));
    } catch (e) { /* ignore */ }
  }, []);

  // Reset detection when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined && resetTrigger > 0) {
      hasDetectedRef.current = false;
      votesRef.current = {};
      setScanStatus('Point camera at barcode');
    }
  }, [resetTrigger]);

  const stopScanner = async () => {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;

    // Cancel animation frame first
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // Pause video before stopping stream
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch (e) { /* ignore */ }
      videoRef.current.srcObject = null;
    }

    // Small delay to let video fully stop
    await new Promise(resolve => setTimeout(resolve, 100));

    // Now stop the stream tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    videoTrackRef.current = null;
    quaggaInitializedRef.current = false;
    setScannerStarted(false);
    setLoading(false);
    setError(null);
    isStoppingRef.current = false;
  };

  const resetState = () => {
    setWaitingForClick(!isMobileDevice());
    setCamerasLoaded(false);
    setBackCameras([]);
    setSelectedCamera('');
    setScannerStarted(false);
    setLoading(false);
    setError(null);
    videoTrackRef.current = null;
    setScanStatus('Point camera at barcode');
    votesRef.current = {};
    hasDetectedRef.current = false;
    quaggaInitializedRef.current = false;
  };

  // Camera enumeration effect
  useEffect(() => {
    const getCameras = async () => {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
        stopStream(tempStream);
        await new Promise(resolve => setTimeout(resolve, 200));

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices
          .filter(d => d.kind === 'videoinput')
          .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` }));

        const backOnly = videoDevices.filter(d => {
          const label = d.label.toLowerCase();
          if (label.includes('front') || label.includes('user') || label.includes('facetime')) return false;
          if (isVirtualCamera(label)) return false;
          return true;
        });

        const camerasToUse = backOnly.length > 0 ? backOnly : videoDevices;
        setBackCameras(camerasToUse);

        if (camerasToUse.length > 0) {
          const savedCameraId = localStorage.getItem(CAMERA_STORAGE_KEY);
          const savedExists = savedCameraId && camerasToUse.some(c => c.deviceId === savedCameraId);
          if (savedExists) {
            setSelectedCamera(savedCameraId!);
          } else {
            const nonWide = camerasToUse.find(d => !d.label.toLowerCase().includes('wide'));
            setSelectedCamera(nonWide?.deviceId || camerasToUse[0].deviceId);
          }
        }
        setCamerasLoaded(true);
      } catch {
        setCamerasLoaded(true);
      }
    };

    if (active && !camerasLoaded && !isStoppingRef.current) {
      getCameras();
    }
  }, [active, camerasLoaded]);

  // Cleanup when deactivated
  useEffect(() => {
    if (!active) {
      stopScanner().then(() => {
        resetState();
      });
    }
  }, [active]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const handleCameraChange = async (deviceId: string) => {
    // Stop current scanner completely
    await stopScanner();

    // Update selected camera
    setSelectedCamera(deviceId);
    try {
      localStorage.setItem(CAMERA_STORAGE_KEY, deviceId);
    } catch (e) { /* ignore */ }

    // Wait a bit longer before starting new camera
    if (!waitingForClick) {
      setTimeout(() => startScanner(deviceId), 300);
    }
  };

  const applyZoom = async (zoomLevel: number) => {
    if (!videoTrackRef.current) return;
    try {
      const actualZoom = Math.min(zoomLevel, maxZoom);
      await videoTrackRef.current.applyConstraints({ advanced: [{ zoom: actualZoom } as any] });
      setCurrentZoom(actualZoom);
      try { localStorage.setItem(ZOOM_STORAGE_KEY, actualZoom.toString()); } catch (e) { /* ignore */ }
    } catch (e) { /* ignore */ }
  };

  const handleZoomChange = (_: React.MouseEvent<HTMLElement>, newZoom: number | null) => {
    if (newZoom !== null) applyZoom(newZoom);
  };

  const processFrame = () => {
    if (!videoRef.current || !canvasRef.current || hasDetectedRef.current) {
      animationRef.current = requestAnimationFrame(processFrame);
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (ctx && video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      Quagga.decodeSingle({
        src: canvas.toDataURL('image/png'),
        numOfWorkers: 0,
        decoder: {
          readers: ['ean_reader', 'upc_reader'],
        },
        locate: true,
        locator: { patchSize: 'medium', halfSample: true },
      }, (result) => {
        if (result?.codeResult?.code) {
          handleDetected(result);
        }
      });
    }

    animationRef.current = requestAnimationFrame(processFrame);
  };

  const handleDetected = (result: any) => {
    if (hasDetectedRef.current) return;
    const code = result?.codeResult?.code;
    if (!code || code.length < 12 || code.length > 13) return;
    const isValid = code.length === 13 ? isValidEAN13(code) : isValidUPCA(code);
    if (!isValid) return;

    votesRef.current[code] = (votesRef.current[code] || 0) + 1;
    const votes = votesRef.current[code];
    setScanStatus(`Detected: ${code} (${votes}/${REQUIRED_VOTES})`);

    if (votes >= REQUIRED_VOTES) {
      hasDetectedRef.current = true;
      setScanStatus(`✓ Confirmed: ${code}`);
      analytics.trackEvent('barcode_scan', 'feature', { code_type: code.length === 13 ? 'EAN13' : 'UPCA' });
      setTimeout(() => onDetectedRef.current(code), 300);
    }
  };

  const configureZoom = async (track: MediaStreamTrack) => {
    try {
      videoTrackRef.current = track;
      const capabilities = track.getCapabilities() as any;

      if (capabilities?.zoom) {
        const max = capabilities.zoom.max || 1;
        setMaxZoom(max);
        const levels = [1, 2, 3, 4].filter(z => z <= max);
        setAvailableZoomLevels(levels);

        const savedZoom = localStorage.getItem(ZOOM_STORAGE_KEY);
        const targetZoom = savedZoom ? parseFloat(savedZoom) : DEFAULT_ZOOM;
        const actualZoom = Math.min(targetZoom, max);
        await track.applyConstraints({ advanced: [{ zoom: actualZoom } as any] });
        setCurrentZoom(actualZoom);
      } else {
        setMaxZoom(1);
        setAvailableZoomLevels([1]);
        setCurrentZoom(1);
      }
    } catch (e) {
      setMaxZoom(1);
      setAvailableZoomLevels([1]);
    }
  };

  const startScanner = async (cameraId?: string) => {
    const deviceId = cameraId || selectedCamera;
    if (!deviceId) {
      setError('No camera selected.');
      return;
    }

    if (isStoppingRef.current) {
      // Wait for stop to complete
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    setLoading(true);
    setError(null);
    setWaitingForClick(false);
    votesRef.current = {};
    hasDetectedRef.current = false;

    try {
      // Get camera stream
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: deviceId },
          width: { min: 640, ideal: 1280, max: 1920 },
          height: { min: 480, ideal: 720, max: 1080 },
        },
        audio: false,
      });

      streamRef.current = stream;

      // Set up video element
      if (videoRef.current) {
        // Make sure video is paused before assigning new stream
        try {
          videoRef.current.pause();
        } catch (e) { /* ignore */ }

        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.muted = true;

        // Wait for video to be ready before playing
        await new Promise<void>((resolve, reject) => {
          const video = videoRef.current!;
          const timeoutId = setTimeout(() => {
            video.removeEventListener('canplay', onCanPlay);
            video.removeEventListener('error', onError);
            resolve(); // Resolve anyway after timeout
          }, 3000);

          const onCanPlay = () => {
            clearTimeout(timeoutId);
            video.removeEventListener('canplay', onCanPlay);
            video.removeEventListener('error', onError);
            resolve();
          };

          const onError = (e: Event) => {
            clearTimeout(timeoutId);
            video.removeEventListener('canplay', onCanPlay);
            video.removeEventListener('error', onError);
            reject(new Error('Video failed to load'));
          };

          video.addEventListener('canplay', onCanPlay);
          video.addEventListener('error', onError);

          // If already ready, resolve immediately
          if (video.readyState >= 3) {
            clearTimeout(timeoutId);
            video.removeEventListener('canplay', onCanPlay);
            video.removeEventListener('error', onError);
            resolve();
          }
        });

        await videoRef.current.play();

        // Configure zoom
        const track = stream.getVideoTracks()[0];
        if (track) {
          await configureZoom(track);
        }

        // Create canvas for frame processing
        if (!canvasRef.current) {
          canvasRef.current = document.createElement('canvas');
        }

        // Start frame processing
        quaggaInitializedRef.current = true;
        setScannerStarted(true);
        setLoading(false);
        setScanStatus('Point camera at barcode');

        // Start the scanning loop
        animationRef.current = requestAnimationFrame(processFrame);
      }

    } catch (err: any) {
      console.error('Scanner error:', err);
      let errorMessage = 'Camera error. Please try again.';

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please allow camera access.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found. Please connect a camera.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is in use or not available.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not meet requirements. Try another camera.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setLoading(false);
      setScannerStarted(false);
      onError?.(errorMessage);
    }
  };

  // Auto-start scanner when conditions are met
  useEffect(() => {
    if (active && camerasLoaded && selectedCamera && !waitingForClick && !scannerStarted && !loading && !quaggaInitializedRef.current && !isStoppingRef.current) {
      startScanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, camerasLoaded, selectedCamera, waitingForClick, scannerStarted, loading]);

  if (!active) return null;

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={() => { setError(null); setWaitingForClick(true); }} sx={{ mb: 2 }}>
          Try Again
        </Button>
        {backCameras.length > 1 && (
          <FormControl fullWidth size="small">
            <InputLabel>Try a different camera</InputLabel>
            <Select
              value={selectedCamera}
              label="Try a different camera"
              onChange={(e) => { setError(null); handleCameraChange(e.target.value); }}
            >
              {backCameras.map((cam) => (
                <MenuItem key={cam.deviceId} value={cam.deviceId}>{cam.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    );
  }

  const showCameraSelector = backCameras.length > 1;
  const showZoomControls = availableZoomLevels.length > 1 && scannerStarted;

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      {showCameraSelector && (
        <Box sx={{ mb: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Camera</InputLabel>
            <Select value={selectedCamera} label="Camera" onChange={(e) => handleCameraChange(e.target.value)}>
              {backCameras.map((cam) => (
                <MenuItem key={cam.deviceId} value={cam.deviceId}>{cam.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Having trouble scanning? Try a different camera.
          </Typography>
        </Box>
      )}

      {waitingForClick && camerasLoaded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Button variant="contained" startIcon={<VideocamIcon />} onClick={() => startScanner()} size="large">
            Start Camera
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click to enable barcode scanning
          </Typography>
        </Box>
      )}

      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, bgcolor: 'grey.100', borderRadius: 1 }}>
          <CircularProgress size={32} sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">Starting camera...</Typography>
        </Box>
      )}

      {/* Show loading state while cameras are being enumerated */}
      {!camerasLoaded && !loading && !error && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, bgcolor: 'grey.100', borderRadius: 1 }}>
          <CircularProgress size={32} sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">Detecting cameras...</Typography>
        </Box>
      )}

      {/* Video container */}
      <Box
        id="barcode-scanner-container"
        ref={scannerRef}
        sx={{
          position: 'relative',
          display: scannerStarted ? 'block' : 'none',
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <video
          ref={videoRef}
          style={{
            display: 'block',
            width: '100%',
            maxHeight: 250,
            objectFit: 'cover',
          }}
          playsInline
          muted
        />

        {/* Overlay */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Box sx={{ width: '80%', height: 100, border: '2px solid', borderColor: scanStatus.startsWith('✓') ? 'success.main' : 'primary.main', borderRadius: 1, position: 'relative' }}>
            {!scanStatus.startsWith('✓') && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, height: 2, bgcolor: 'error.main', animation: 'scan 2s linear infinite', '@keyframes scan': { '0%': { top: 0 }, '50%': { top: '100%' }, '100%': { top: 0 } } }} />
            )}
          </Box>
        </Box>

        {/* Status */}
        <Typography sx={{ position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center', color: 'white', textShadow: '0 1px 3px rgba(0,0,0,0.9)', fontSize: 14, fontWeight: 'bold', pointerEvents: 'none' }}>
          {scanStatus}
        </Typography>

        {/* Zoom */}
        {showZoomControls && (
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <ToggleButtonGroup
              value={currentZoom}
              exclusive
              onChange={handleZoomChange}
              size="small"
              sx={{
                bgcolor: 'rgba(0,0,0,0.6)',
                '& .MuiToggleButton-root': {
                  color: 'white', borderColor: 'rgba(255,255,255,0.3)', px: 1, py: 0.5, minWidth: 36, fontSize: 12,
                  '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                },
              }}
            >
              {availableZoomLevels.map((level) => (
                <ToggleButton key={level} value={level}>{level}x</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BarcodeScanner;